import React, { useRef } from "react";
import { Link } from "react-router-dom";
import SiteFooter from "../components/layout/SiteFooter";
import SiteNavbar from "../components/layout/SiteNavbar";
import useRevealOnScroll from "../components/common/useRevealOnScroll";
import fispLogo from "../assets/logo-iniciales-oscuro.png";
import ubaLogo from "../assets/media/img/UBA.png";

const fispUbaNavTheme = {
    primary: "#0f6ea8",
    primaryDark: "#0f2f5f",
    primaryLight: "#7dd3fc",
    pale: "#e0f2fe",
};

function AsideIcon({ type }) {
    const commonProps = {
        viewBox: "0 0 24 24",
        fill: "none",
        stroke: "currentColor",
        strokeWidth: 1.9,
        strokeLinecap: "round",
        strokeLinejoin: "round",
        "aria-hidden": true,
    };

    switch (type) {
        case "diploma":
            return (
                <svg {...commonProps}>
                    <path d="m7 6.5 5-2.5 5 2.5v6.2c0 3-1.9 5.5-5 6.8-3.1-1.3-5-3.8-5-6.8V6.5Z" />
                    <path d="m10 12 1.4 1.4 2.8-3" />
                </svg>
            );
        case "clipboard":
            return (
                <svg {...commonProps}>
                    <rect x="6" y="5.5" width="12" height="15" rx="2" />
                    <path d="M9 5.5h6" />
                    <path d="M10 3.8h4a1 1 0 0 1 1 1v1.7H9V4.8a1 1 0 0 1 1-1Z" />
                    <path d="M9.5 11h5" />
                    <path d="M9.5 14.5h5" />
                </svg>
            );
        case "globe":
            return (
                <svg {...commonProps}>
                    <circle cx="12" cy="12" r="8" />
                    <path d="M4.5 12h15" />
                    <path d="M12 4.5a12 12 0 0 1 0 15" />
                    <path d="M12 4.5a12 12 0 0 0 0 15" />
                </svg>
            );
        case "laptop":
            return (
                <svg {...commonProps}>
                    <rect x="6" y="5.5" width="12" height="8.5" rx="1.5" />
                    <path d="M4.5 17.5h15" />
                    <path d="M7.5 17.5 9 19h6l1.5-1.5" />
                </svg>
            );
        case "document":
            return (
                <svg {...commonProps}>
                    <path d="M8 4.5h6l3 3v12H8a2 2 0 0 1-2-2v-11a2 2 0 0 1 2-2Z" />
                    <path d="M14 4.5v3h3" />
                    <path d="M9.5 11h5" />
                    <path d="M9.5 14.5h5" />
                </svg>
            );
        default:
            return null;
    }
}

export default function FispUba() {
    const pageRef = useRef(null);
    useRevealOnScroll(pageRef);
    return (
        <div className="fisp-uba" ref={pageRef}>
            <SiteNavbar activeSection="fisp-uba" navTheme={fispUbaNavTheme} />
            <section className="hero">
                <div className="hero-mesh"></div>
                <div className="hero-inner">
                    <div className="hero-bread">
                        <Link to="/">Inicio </Link> → FISP + UBA
                    </div>
                    <div className="hero-logos">
                        <img
                            src={fispLogo}
                            alt="FISP"
                            style={{
                                filter: "brightness(0) invert(1)",
                            }}
                        />
                        <span className="plus">+</span>
                        <img
                            className="uba-logo"
                            src={ubaLogo}
                            alt="UBA"
                            style={{
                                height: "52px",
                            }}
                        />
                    </div>
                    <h1>
                        Convenio Marco de Cooperación Académica con la{" "}
                        <span>Facultad de Medicina de la UBA</span>
                    </h1>
                    <p className="hero-sub">Formación de excelencia respaldada por una de las instituciones académicas más prestigiosas de Latinoamérica.</p>
                </div>
            </section>
            <section className="content reveal">
                <div className="content-inner">
                    <div>
                        <span className="content-tag">El convenio</span>
                        <h2>Dos instituciones comprometidas con la formación de profesionales de la salud del más alto nivel</h2>
                        <p>
                            Desde 2021, la Fundación Iberoamericana de Salud Pública y la Facultad de Medicina de la Universidad de Buenos Aires sostienen un Convenio Marco de
                            Asistencia Técnica y Capacitación que articula los esfuerzos de ambas instituciones en la generación, el desarrollo y el patrocinio de actividades
                            orientadas a mejorar las condiciones sanitarias de la comunidad y a dar respuesta a los desafíos que enfrenta la salud pública en la región.
                        </p>
                        <p>
                            Este acuerdo establece un marco de trabajo conjunto en el que la Facultad de Medicina aporta la dirección académica, la supervisión docente y la
                            coordinación curricular de cada programa, mientras que FISP contribuye con la estructura organizativa, la plataforma tecnológica y los recursos
                            necesarios para llevar la formación a profesionales de más de doce países. Cada programa de posgrado, diplomatura y curso que se dicta bajo este
                            convenio cuenta con el respaldo y la doble titulación de ambas instituciones.
                        </p>
                        <p>
                            La alianza impulsa la integración de docencia, investigación y asistencia como ejes transversales de cada propuesta formativa, promoviendo la equidad en
                            el acceso a educación de calidad y la implementación de nuevas tecnologías para el desarrollo de la salud pública. Es un compromiso compartido por
                            fortalecer los recursos humanos del sector salud con estándares que estén a la altura de los avances científicos y tecnológicos de nuestro tiempo.
                        </p>
                        <p>
                            Para quienes eligen formarse en FISP, este convenio representa mucho más que un sello institucional: es la garantía de que cada contenido, cada docente
                            y cada evaluación cuentan con la supervisión y el aval de una de las facultades de medicina más reconocidas de América Latina, con más de 200 años de
                            trayectoria formando profesionales que transforman la salud de la región.
                        </p>
                    </div>
                    <div className="content-aside">
                        <h3>¿Qué significa para vos como alumno/a?</h3>
                        <div className="aside-item">
                            <div className="aside-ico aside-ico-diploma"><AsideIcon type="diploma" /></div>
                            <div className="aside-text">
                                <h4>Doble titulación</h4>
                                <p>Certificado de FISP y de la Facultad de Medicina de la Universidad de Buenos Aires.</p>
                            </div>
                        </div>
                        <div className="aside-item">
                            <div className="aside-ico aside-ico-clipboard"><AsideIcon type="clipboard" /></div>
                            <div className="aside-text">
                                <h4>Supervisión académica UBA</h4>
                                <p>Cada programa es dirigido y coordinado por la Facultad de Medicina.</p>
                            </div>
                        </div>
                        <div className="aside-item">
                            <div className="aside-ico aside-ico-globe"><AsideIcon type="globe" /></div>
                            <div className="aside-text">
                                <h4>Alcance internacional</h4>
                                <p>Formación 100% online con reconocimiento en más de 12 países de Iberoamérica.</p>
                            </div>
                        </div>
                        <div className="aside-item">
                            <div className="aside-ico aside-ico-laptop"><AsideIcon type="laptop" /></div>
                            <div className="aside-text">
                                <h4>Tecnología + excelencia</h4>
                                <p>Plataforma virtual de última generación con contenidos actualizados permanentemente.</p>
                            </div>
                        </div>
                        <div className="aside-item">
                            <div className="aside-ico aside-ico-document"><AsideIcon type="document" /></div>
                            <div className="aside-text">
                                <h4>Convenio vigente desde 2021</h4>
                                <p>Marco institucional formal aprobado por resolución del Decanato de la Facultad de Medicina.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            <section className="cta">
                <h2>Formá parte de la comunidad FISP + UBA</h2>
                <p>Inscripciones abiertas para todos los programas 2026.</p>
                <div className="cta-btns">
                    <Link to="/educativo" className="btn-w">
                        Explorar programas académicos
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <path d="M5 12h14M12 5l7 7-7 7"></path>
                        </svg>
                    </Link>
                    <a href="https://wa.me/5491123879118" className="btn-ghost-w">
                        Consultá por WhatsApp
                    </a>
                </div>
            </section>
            <SiteFooter />
        </div>
    );
}
